import React, { useCallback, useEffect, useState, useMemo } from "react";
import TableContainer from "Common/TableContainer";
import { Loader } from "../../Common/Loader";
import { Pencil, Trash2, Plus, Search } from "lucide-react";
import DeleteModal from "Common/DeleteModal";
import { ToastContainer } from "react-toastify";
import { Common } from "helpers/common";
import moment from "moment";
import { AddStudent } from "./AddStudent";
import { getStudentList, deleteStudent } from "helpers/backend_helper";
import Select from "react-select";
import { useProgramStore } from "store/useProgramStore";
import { useClassStore } from "store/useClassStore";

// Default options for dropdowns
const defaultProgramOption = { value: "", label: "All Programs" };
const defaultClassOption = { value: "", label: "All Classes" };

function StudentList() {

  // Zustand stores
  const { 
    programs,
    selectedProgram, 
    setSelectedProgram,
    fetchPrograms
  } = useProgramStore();
  
  const { 
    classes,
    selectedClass, 
    setSelectedClass,
  } = useClassStore();
  const [students, setStudents] = useState<any[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<any[]>([]);
  const [eventData, setEventData] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFiltering, setIsFiltering] = useState<boolean>(false);
  const [searchText, setSearchText] = useState('');
  const [show, setShow] = useState<boolean>(false);
  const [studentData, setStudentData] = useState<any>({});
  const uiHelper = new Common();

  // Use Zustand store data directly - convert to dropdown format
  const programOptions = useMemo(() => {
    const options = programs.map((program: any) => ({
      value: program.programId,
      label: program.programName
    }));
    return [defaultProgramOption, ...options];
  }, [programs]);

  const classOptions = useMemo(() => {
    const options = classes.map((classItem: any) => ({
      value: classItem.classId,
      label: classItem.className
    }));
    return [defaultClassOption, ...options];
  }, [classes]);

  // Convert selected values from Zustand store to dropdown format
  const selectedProgramOption = useMemo(() => {
    if (!selectedProgram) return defaultProgramOption;
    return {
      value: selectedProgram.programId,
      label: selectedProgram.programName
    };
  }, [selectedProgram]);

  const selectedClassOption = useMemo(() => {
    if (!selectedClass) return defaultClassOption;
    return {
      value: selectedClass.classId,
      label: selectedClass.className
    };
  }, [selectedClass]);

  // Delete Modal
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const deleteToggle = () => setDeleteModal(!deleteModal);

  // useEffect(() => {
  //   document.body.classList.remove('overflow-hidden');
  //   loadStudents(false);
    
  //   // Load data from Zustand stores if not already loaded
  //   const loadData = async () => {
  //     console.log("Starting to load dropdown data...");
      
  //     // Ensure default selections are set if not already selected
  //     if (programs.length > 0 && !selectedProgram) {
  //       setSelectedProgram(programs[0]);
  //       console.log("Set default program:", programs[0]);
  //     }
      
  //     if (classes.length > 0 && !selectedClass) {
  //       setSelectedClass(classes[0]);
  //       console.log("Set default class:", classes[0]);
  //     }
      
  //     console.log("Finished loading dropdown data");
  //   };

  //   loadData();
  // }, [programs.length, classes.length, selectedProgram, selectedClass, setSelectedProgram, setSelectedClass]);


  // Load students when selectedProgram or selectedClass changes
  // but only if both are actually available
  useEffect(() => {
    if (selectedProgram && selectedClass) {
      console.log("Loading students with Program:", selectedProgram.programId, "Class:", selectedClass.classId);
      loadStudents(true);
    }
  }, [selectedProgram?.programId, selectedClass?.classId]);

  // Ensure default selections when stores are loaded but avoid duplicate API calls
  useEffect(() => {
    if (programs.length > 0 && !selectedProgram) {
      console.log("Auto-selecting first program:", programs[0]);
      setSelectedProgram(programs[0]);
    }
  }, [programs.length]); // Use length instead of whole array to prevent unnecessary triggers

  useEffect(() => {
    if (classes.length > 0 && !selectedClass) {
      console.log("Auto-selecting first class:", classes[0]);
      setSelectedClass(classes[0]);
    }
  }, [classes.length]); // Use length instead of whole array to prevent unnecessary triggers

  // Apply filters when students data or search text changes
  // useEffect(() => {
  //   if (students.length > 0) {
  //     applyFilters();
  //   }
  // }, [students, searchText]);

  async function loadStudents(reload: boolean) {
    try {
      setIsLoading(true);
      const programId = selectedProgram?.programId || 0;
      const classId = selectedClass?.classId || 0;
      
      console.log("API Call: getStudentList with Program ID:", programId, "Class ID:", classId);
      const response = await getStudentList(programId, classId);
      
      console.log("Student API Response:", response);
      setStudents(response.data || []);
      setFilteredStudents(response.data || []);
    } catch (error) {
      console.error("Error loading students:", error);
      setStudents([]);
      setFilteredStudents([]);
    } finally {
      setIsLoading(false);
    }
  }

  // Delete Data
  const onClickDelete = (cell: any) => {
    setDeleteModal(true);
    if (cell.studentId) {
      setEventData(cell);
    }
  };

  const handleDelete = async () => {
    if (eventData) {
      try {
        await deleteStudent(eventData);
        setDeleteModal(false);
        uiHelper.showSuccessMessage("Student has been deleted successfully.");

        // remove the item and update list
        setStudents((prev) =>
          prev.filter((item) => item.studentId !== eventData.studentId)
        );
        setFilteredStudents((prev) =>
          prev.filter((item) => item.studentId !== eventData.studentId)
        );
      } catch (error) {
        console.error("Error deleting student:", error);
        uiHelper.showErrorMessage("Error deleting student");
      }
    }
  };

  const handleProgramChange = async (option: any) => {
    setIsFiltering(true);
    
    try {
      // Option is already in the format {value: programId, label: programName}
      if (option && option.value && option.value !== "") {
        // Find the program from Zustand store and update it
        const program = programs.find(p => p.programId === option.value);
        if (program) {
          setSelectedProgram(program);
          console.log("Program selected:", program);
        }
      } else {
        setSelectedProgram(null);
        console.log("Program cleared");
      }
      
      // Small delay to prevent flickering
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Note: loadStudents will be called automatically by the useEffect when selectedProgram changes
    } finally {
      setIsFiltering(false);
    }
  };

  const handleClassChange = async (option: any) => {
    setIsFiltering(true);
    
    try {
      // Option is already in the format {value: classId, label: className}
      if (option && option.value && option.value !== "") {
        // Find the class from Zustand store and update it
        const classItem = classes.find(c => c.classId === option.value);
        if (classItem) {
          setSelectedClass(classItem);
          console.log("Class selected:", classItem);
        }
      } else {
        setSelectedClass(null);
        console.log("Class cleared");
      }
      
      // Small delay to prevent flickering
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Note: loadStudents will be called automatically by the useEffect when selectedClass changes
    } finally {
      setIsFiltering(false);
    }
  };

  const filterSearchData = (e: any) => {
    const searchText = e.target.value;
    setSearchText(searchText);
    // Search filtering is handled by applyFilters in useEffect
  };

  const applyFilters = (searchTerm?: string) => {
    const search = searchTerm !== undefined ? searchTerm : searchText;
    let filteredData = [...students];

    // Apply search filter only (program and class filtering is done server-side)
    if (search.length > 0) {
      filteredData = filteredData.filter(
        (item) =>
          item.firstName.toLowerCase().includes(search.toLowerCase()) ||
          item.lastName.toLowerCase().includes(search.toLowerCase()) ||
          item.email.toLowerCase().includes(search.toLowerCase()) ||
          item.phone1.toLowerCase().includes(search.toLowerCase()) ||
          (item.guardian && item.guardian.toLowerCase().includes(search.toLowerCase()))
      );
    }

    setFilteredStudents(filteredData);
  };

  const handleAddStudent = () => {
    setStudentData({});
    toggle();
  };

  const handleEdit = (data: any) => {
    setStudentData(data);
    toggle();
  };

  const onSave = (result: any) => {
    console.log("Save result:", result);
    loadStudents(true);
    toggle();
  };

  const toggle = useCallback(() => {
    setShow(!show);
    if (show) {
      setStudentData(null);
    }
  }, [show]);

  const columns = [
    {
      header: "First Name",
      accessorKey: "firstName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Last Name",
      accessorKey: "lastName",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Email",
      accessorKey: "email",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Phone",
      accessorKey: "phone1",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Gender",
      accessorKey: "gender",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: "Guardian",
      accessorKey: "guardian",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.guardian || '-'}</span>
      ),
    },
    {
      header: "Start Date",
      accessorKey: "startDate",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.startDate ? moment(row.original.startDate).format("MMM DD, YYYY") : '-'}</span>
      ),
    },
    {
      header: "DOB",
      accessorKey: "dob",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{row.original.dob ? moment(row.original.dob).format("MMM DD, YYYY") : '-'}</span>
      ),
    },
    {
      header: "Registration Fee",
      accessorKey: "registrationFee",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>${row.original.registrationFee?.toFixed(2) || '0.00'}</span>
      ),
    },
    {
      header: "Fees",
      accessorKey: "fees",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>${row.original.fees?.toFixed(2) || '0.00'}</span>
      ),
    },
    {
      header: "Created On",
      accessorKey: "createdOn",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span>{moment(row.original.createdOn).format("MMM DD, YYYY")}</span>
      ),
    },
    {
      header: "Actions",
      id: "actions",
      enableColumnFilter: false,
      enableSorting: false,
      cell: ({ row }: any) => {
        const student = row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 text-blue-500 bg-blue-100 rounded hover:bg-blue-500 hover:text-white transition-colors duration-200"
              onClick={() => handleEdit(student)}
            >
              <Pencil className="size-4" />
            </button>
            <button
              type="button"
              className="inline-flex items-center justify-center w-8 h-8 text-red-500 bg-red-100 rounded hover:bg-red-500 hover:text-white transition-colors duration-200"
              onClick={() => onClickDelete(student)}
            >
              <Trash2 className="size-4" />
            </button>
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <ToastContainer closeButton={false} limit={1} />
      <div className="grid grid-cols-1 gap-x-5 xl:grid-cols-12" style={{ marginTop: 15 }}>
        <div className="xl:col-span-12">
          <div className="card" id="studentTable">

            <div className="card-body">
              {/* Filters Section */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 xl:grid-cols-12 mb-5">
                
                {/* Search */}
                <div className="lg:col-span-3">
                  <div className="relative">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search for ..."
                      autoComplete="off"
                      value={searchText}
                      onChange={filterSearchData}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>

                {/* Program Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Program"
                    name="program"
                    options={programOptions}
                    value={selectedProgramOption}
                    onChange={handleProgramChange}
                    isLoading={programs.length === 0 || isFiltering}
                    isDisabled={isFiltering}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Class Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Class"
                    name="class"
                    options={classOptions}
                    value={selectedClassOption}
                    onChange={handleClassChange}
                    isLoading={classes.length === 0 || isFiltering}
                    isDisabled={isFiltering}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Add Student Button */}
                <div className="lg:col-span-3">
                  <button
                    type="button"
                    className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600 hover:border-custom-600 focus:text-white focus:bg-custom-600 focus:border-custom-600 focus:ring focus:ring-custom-100 active:text-white active:bg-custom-600 active:border-custom-600 active:ring active:ring-custom-100 dark:ring-custom-400/20 w-full"
                    onClick={handleAddStudent}
                  >
                    <Plus className="inline-block size-4" />{" "}
                    <span className="align-middle">Add Student</span>
                  </button>
                </div>
              </div>
            </div>


            {isLoading ? (
              <Loader />
            ) : (
              <div className="relative">
                {isFiltering && (
                  <div className="absolute inset-0 bg-white/50 dark:bg-zink-800/50 flex items-center justify-center z-10 rounded-md">
                    <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-zink-300">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-custom-500 border-t-transparent"></div>
                      Filtering...
                    </div>
                  </div>
                )}
                <TableContainer
                  columns={columns}
                  data={filteredStudents}
                  isPagination={true}
                  customPageSize={15}
                  divclassName="overflow-x-auto"
                  tableclassName="w-full whitespace-nowrap"
                  theadclassName="ltr:text-left rtl:text-right bg-custom-200 dark:bg-zink-600"
                  thclassName="px-2.5 py-1.5 font-semibold border-b border-slate-200 dark:border-zink-500"
                  tdclassName="px-2.5 py-1.0 border-y border-slate-200 dark:border-zink-500"
                  trclassName="even:bg-custom-50  dark:odd:bg-zink-700 dark:even:bg-zink-600"
                  PaginationClassName="flex flex-col items-center gap-4 px-4 mt-3 md:flex-row"
                />
              </div>
            )}

          </div>
        </div>
      </div>


      {studentData ? (
        <AddStudent 
          show={show} 
          onHide={toggle} 
          data={studentData} 
          onSave={onSave}
        />) : (<></>)}

      {/* Delete Modal */}
      <DeleteModal
        show={deleteModal}
        onHide={deleteToggle}
        onDelete={handleDelete}
      />
    </React.Fragment>
  );
}

export default StudentList;

